import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

// デフォルトの2FA設定状態
const defaultObj = {
    loading: false,
    data: null,
};

// 2FA設定の状態型
type Setup2FA = {
    loading: boolean;
    data: {
        otpauth_uri: string;
        secret: string;
    } | null;
};

// フックの戻り値の型定義
type ReturnType = [Setup2FA, () => void, () => void];

/**
 * 2要素認証のセットアップを管理するカスタムフック
 * @return {Setup2FA} multiFactorAuth - 2FA設定の状態
 * @return {Function} request - 2FAセットアップリクエストを実行する関数
 * @return {Function} resetState - 状態をリセットする関数
 */
export const useSetup2FA = (): ReturnType => {
    const dispatch = useDispatch();
    // eslint-disable-next-line no-undef
    const mfaSetupConnection = useAppSelector<MFASetupConnection>(
        (state) => state.mfaSetupConnection,
    );
    const [multiFactorAuth, setMultiFactorAuth] =
        useState<Setup2FA>(defaultObj);

    // コネクション状態の変化を処理する関数
    const effect = useCallback((connection: MFASetupConnection) => {
        const { meta, payload } = connection;
        switch (meta.status) {
            case Actions.statusEnum.SUCCESS:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                    data: payload.result,
                }));
                break;
            case Actions.statusEnum.FAILURE:
            case Actions.statusEnum.ERROR:
            case Actions.statusEnum.REQUEST:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                }));
                break;
            default:
                // デフォルトケースの処理
                break;
        }
    }, []);

    useEffect(() => {
        if (mfaSetupConnection) {
            effect(mfaSetupConnection);
        }
    }, [mfaSetupConnection, effect]);

    // 2FAセットアップのリクエストを送信
    const request = useCallback(() => {
        setMultiFactorAuth((prev) => ({
            ...prev,
            loading: true,
        }));

        const setup = Actions.http.connection.authentication.setup;
        dispatch(setup());
    }, [dispatch]);

    // 状態をデフォルト値にリセット
    const resetState = useCallback(() => {
        setMultiFactorAuth({ ...defaultObj });
    }, []);

    return [multiFactorAuth, request, resetState];
};
